"use client";

import React, { useEffect, useRef } from "react";
import { Provider } from "react-redux";
import { store } from "@/store";
import { injectStore } from "@/services/ApiService";
import { setTheme, THEME_STORAGE_KEY } from "@/store/slices/themeSlice";
import { useAppDispatch, useAppSelector } from "@/store/hooks";

function ThemePersistence() {
  const dispatch = useAppDispatch();
  const { mode } = useAppSelector((state) => state.theme);
  const hasHydrated = useRef(false);

  // Hydrate from localStorage on mount
  useEffect(() => {
    const storedMode = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (storedMode === "light" || storedMode === "dark") {
      dispatch(setTheme(storedMode));
    }
    hasHydrated.current = true;
  }, [dispatch]);

  // Keep localStorage in sync
  useEffect(() => {
    if (!hasHydrated.current) return;
    window.localStorage.setItem(THEME_STORAGE_KEY, mode);
  }, [mode]);

  // Sync data-theme on <html> + body bg so CSS variables stay correct
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", mode);
    document.documentElement.style.backgroundColor =
      mode === "light" ? "#f8fafc" : "#0B0F19";
  }, [mode]);

  return null;
}

export function ReduxProvider({ children }: { children: React.ReactNode }) {
  // Inject store into ApiService to break circular dependency
  injectStore(store);

  return (
    <Provider store={store}>
      <ThemePersistence />
      {children}
    </Provider>
  );
}
